import { FC, useEffect, useState } from 'react'
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Stack,
  TextField,
} from '@mui/material'
import type { BaseTributo, NomeTributo, PoliticaTributariaRequest } from '@/types/simulacaoComissao'
import { NOMES_TRIBUTO, ROTULO_BASE_TRIBUTO } from '@/types/simulacaoComissao'
import { lerPercentualPtBr, percentualParaFracao } from '../fracao'
import { errosDaFalha, HELPER_ORIGEM } from './errosFormulario'
import { inicioPadraoProposta } from './inicioPadrao'

interface Props {
  aberto: boolean
  onFechar: () => void
  onSalvar: (request: PoliticaTributariaRequest) => Promise<void>
}

const BASE_UNICA: BaseTributo = 'COMISSAO_BRUTA'

/**
 * Nova versão de alíquota (CSLL, IR ou outro) sobre a comissão bruta. A alíquota é digitada
 * em PERCENTUAL e vai como FRAÇÃO. Sem valor padrão — alíquota é dado do Financeiro/Jurídico.
 */
export const NovoTributoDialog: FC<Props> = ({ aberto, onFechar, onSalvar }) => {
  const [nome, setNome] = useState<NomeTributo | ''>('')
  const [descricao, setDescricao] = useState('')
  const [aliquota, setAliquota] = useState('')
  const [vigenciaInicio, setVigenciaInicio] = useState(() => inicioPadraoProposta())
  const [vigenciaFim, setVigenciaFim] = useState('')
  const [origem, setOrigem] = useState('')
  const [erros, setErros] = useState<Record<string, string>>({})
  const [erroGeral, setErroGeral] = useState<string | null>(null)
  const [salvando, setSalvando] = useState(false)

  useEffect(() => {
    if (aberto) {
      setNome('')
      setDescricao('')
      setAliquota('')
      setVigenciaInicio(inicioPadraoProposta())
      setVigenciaFim('')
      setOrigem('')
      setErros({})
      setErroGeral(null)
    }
  }, [aberto])

  const salvar = async () => {
    const faltando: Record<string, string> = {}
    if (!nome) faltando.nome = 'Escolha o tributo'
    if (nome === 'OUTRO' && !descricao.trim()) faltando.descricao = 'Descreva o tributo "outro"'
    const aliquotaPercentual = lerPercentualPtBr(aliquota)
    if (aliquotaPercentual == null) faltando.aliquota = 'Informe a alíquota em percentual, com vírgula (ex.: 9)'
    if (!vigenciaInicio) faltando.vigenciaInicio = 'Informe o início da vigência'
    if (!origem.trim()) faltando.origem = 'Informe a origem (lei, ata ou parecer)'
    if (Object.keys(faltando).length) {
      setErros(faltando)
      return
    }
    setSalvando(true)
    setErros({})
    setErroGeral(null)
    try {
      await onSalvar({
        nome: nome as NomeTributo,
        descricao: descricao.trim() || null,
        aliquota: percentualParaFracao(aliquotaPercentual as number),
        base: BASE_UNICA,
        vigenciaInicio,
        vigenciaFim: vigenciaFim || null,
        origem: origem.trim(),
      })
      onFechar()
    } catch (e) {
      const r = errosDaFalha(e)
      setErros(r.porCampo)
      setErroGeral(r.geral)
    } finally {
      setSalvando(false)
    }
  }

  return (
    <Dialog open={aberto} onClose={salvando ? undefined : onFechar} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 600 }}>Propor versão — alíquota de tributo</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {erroGeral && <Alert severity="error">{erroGeral}</Alert>}
          <TextField
            select
            SelectProps={{ native: true }}
            label="Tributo"
            value={nome}
            onChange={(e) => setNome(e.target.value as NomeTributo | '')}
            error={!!erros.nome}
            helperText={erros.nome}
            InputLabelProps={{ shrink: true }}
            fullWidth
          >
            <option value="">Selecione…</option>
            {NOMES_TRIBUTO.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </TextField>
          <TextField
            label={nome === 'OUTRO' ? 'Descrição' : 'Descrição (opcional)'}
            value={descricao}
            onChange={(e) => setDescricao(e.target.value)}
            error={!!erros.descricao}
            helperText={erros.descricao ?? 'Ex.: IRPJ, PIS/COFINS'}
            inputProps={{ maxLength: 120 }}
            required={nome === 'OUTRO'}
            fullWidth
          />
          <TextField
            label="Alíquota"
            value={aliquota}
            onChange={(e) => setAliquota(e.target.value)}
            error={!!erros.aliquota}
            helperText={erros.aliquota ?? 'Percentual com vírgula, ex.: 9 ou 1,65 (a API guarda como fração)'}
            inputProps={{ inputMode: 'decimal' }}
            InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
            required
            fullWidth
          />
          <TextField
            label="Base de cálculo"
            value={ROTULO_BASE_TRIBUTO[BASE_UNICA]}
            InputProps={{ readOnly: true }}
            helperText="Única base aceita pelo motor nesta versão"
            fullWidth
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Início da vigência"
              type="date"
              value={vigenciaInicio}
              onChange={(e) => setVigenciaInicio(e.target.value)}
              error={!!erros.vigenciaInicio}
              helperText={erros.vigenciaInicio}
              InputLabelProps={{ shrink: true }}
              required
              fullWidth
            />
            <TextField
              label="Fim da vigência (opcional)"
              type="date"
              value={vigenciaFim}
              onChange={(e) => setVigenciaFim(e.target.value)}
              error={!!erros.vigenciaFim}
              helperText={erros.vigenciaFim ?? 'Vigência semiaberta: vale até o dia anterior'}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Stack>
          <TextField
            label="Origem"
            value={origem}
            onChange={(e) => setOrigem(e.target.value)}
            error={!!erros.origem}
            helperText={erros.origem ?? HELPER_ORIGEM}
            inputProps={{ maxLength: 200 }}
            required
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={onFechar} disabled={salvando}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={() => void salvar()}
          disabled={salvando}
          startIcon={salvando ? <CircularProgress size={18} /> : undefined}
        >
          Enviar para aprovação
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default NovoTributoDialog
